import { subDays, subMonths } from 'date-fns';
import { useRef } from 'react';
import { UseMutateAsyncFunction } from 'react-query';
import { generateRandomId } from '../../../helpers';
import { useStore } from '../../../store';
import { IServerErrorResponse } from '../../../types';
import { IFetchTransactions } from '../interfaces';
import { LiliTransaction } from '../types/lili-transaction';
import { useDates } from './useDates';
import { UseFetchTransactionsProps } from './useFetchTransactions';

export enum ETypesFilter {
  NONE = 'none',
  ATMS = 'ATM',
  PURCHASES = 'PURCHASE',
  CHECK_DEPOSITS = 'CHECK_DEPOSIT',
  FUND_TRANSFER = 'FUND_TRANSFER',
  REFUND = 'REFUND',
}

enum EDatePreset {
  ANY = 'any',
  LAST_30_DAYS = 'last30Days',
  LAST_60_DAYS = 'last60Days',
  LAST_6_MONTH = 'last6Month',
  CURRENT_YEAR = 'currentYear',
  LAST_YEAR = 'lastYear',
  CUSTOM = 'custom',
}
interface IDateFilter {
  DATE_PRESET: EDatePreset;
  FROM?: Date | null;
  TO?: Date | null;
}

interface IAmountFilter {
  FROM: number | null;
  TO: number | null;
}

export enum ETransactionsState {
  None = 'none',
  Loading = 'loading',
  Refreshing = 'refreshing',
  LoadingMore = 'loadingMore',
  Loaded = 'loaded',
  FullyLoaded = 'fullyLoaded',
  Failed = 'failed',
}

interface TransactionsListState {
  state: ETransactionsState;
  currentToDate: Date | null;
  currentFromDate: Date | null;
  currentPage: number;
  failureError: string | null;
  failureCount: number;
  loadPending: boolean | null;
  sections: {
    pending: LiliTransaction[];
    completed: LiliTransaction[];
  };
}

const listState: TransactionsListState = {
  currentToDate: null,
  currentFromDate: null,
  currentPage: 1,
  failureError: null,
  failureCount: 0,
  loadPending: true,
  state: ETransactionsState.None,
  sections: {
    pending: [],
    completed: [],
  },
};

let typeFilter: ETypesFilter = ETypesFilter.NONE;

let amountFilter: IAmountFilter = {
  FROM: null,
  TO: null,
};

const dateFilter: IDateFilter = {
  DATE_PRESET: EDatePreset.ANY,
  FROM: null,
  TO: null,
};

export interface UseTransactionsDataProps {
  fetchTransactions: UseMutateAsyncFunction<IFetchTransactions, unknown, UseFetchTransactionsProps, unknown>;
}

export const useTransactionsData = (props: UseTransactionsDataProps) => {
  const { fetchTransactions } = props;
  const pageMaxSize: number = 50;
  const pageMinSize: number = 12;
  const { customer } = useStore((state) => state.summary);
  const setTransactionsList = useStore((state) => state.setTransactionsList);
  const setSelectedTransaction = useStore((state) => state.setSelectedTransaction);
  // const [localTransactions, setLocalTransactions] = useState<LiliTransaction[]>([]);
  const localTransactionsRef = useRef<LiliTransaction[]>([]);
  const {
    getEndOfDay,
    getToDateOfLastYear,
    getMaxDate,
    getStartOfDay,
    getSubDaysFromBaseDate,
    getSubMonthsFromBaseDate,
    startOfYear,
    subYears,
    isSmallerYearThanCurrentYear,
    format,
    isSameDate,
    formatToIsoString,
  } = useDates();
  const currFetchId = useRef<string>('');

  /** State end */

  const getToDate = (): Date => {
    switch (dateFilter.DATE_PRESET) {
      case EDatePreset.ANY:
      case EDatePreset.LAST_30_DAYS:
      case EDatePreset.LAST_60_DAYS:
      case EDatePreset.LAST_6_MONTH:
      case EDatePreset.CURRENT_YEAR:
        return getEndOfDay(new Date());

      case EDatePreset.LAST_YEAR:
        return getToDateOfLastYear(getEndOfDay(new Date()));

      case EDatePreset.CUSTOM:
        return getEndOfDay(dateFilter.TO || new Date());

      default:
        return getEndOfDay(new Date());
    }
  };
  const getFromDate = (): Date => {
    switch (dateFilter.DATE_PRESET) {
      case EDatePreset.ANY:
        return getStartOfDay(new Date(customer.tcTime));

      case EDatePreset.LAST_30_DAYS:
        return getStartOfDay(getSubDaysFromBaseDate(new Date(), 30));

      case EDatePreset.LAST_60_DAYS:
        return getStartOfDay(getSubDaysFromBaseDate(new Date(), 60));

      case EDatePreset.LAST_6_MONTH:
        return getStartOfDay(getSubMonthsFromBaseDate(new Date(), 6));

      case EDatePreset.CURRENT_YEAR:
        return getStartOfDay(startOfYear(new Date()));

      case EDatePreset.LAST_YEAR:
        return getStartOfDay(startOfYear(subYears(new Date(), 1)));

      case EDatePreset.CUSTOM:
        return getStartOfDay(new Date(dateFilter.FROM || customer.tcTime));

      default:
        return getStartOfDay(new Date(customer.tcTime));
    }
  };

  const localizedString = (): string => {
    switch (dateFilter.DATE_PRESET) {
      case EDatePreset.ANY:
        return 'All Transactions';
      case EDatePreset.LAST_30_DAYS:
        return 'Past 30 Days';

      case EDatePreset.LAST_60_DAYS:
        return 'Past 60 Days';

      case EDatePreset.LAST_6_MONTH:
        return 'Last 6 Months';

      case EDatePreset.CURRENT_YEAR:
        return format(new Date(), 'yyyy');

      case EDatePreset.LAST_YEAR:
        return format(subYears(new Date(), 1), 'yyyy');

      case EDatePreset.CUSTOM:
        return `${format(new Date(dateFilter.FROM || ''), 'MM/dd/yyyy')} - ${format(new Date(dateFilter.TO || ''), 'MM/dd/yyyy')}`;

      default:
        return '';
    }
  };

  const currentUserHasLastYear = () => isSmallerYearThanCurrentYear(customer.tcTime);

  const changeStateToLoadSettledTransactions = () => {
    const toDate = getToDate();
    listState.currentToDate = toDate;
    listState.currentFromDate = getMaxDate(getStartOfDay(getSubDaysFromBaseDate(toDate, 30)), getFromDate());
    listState.currentPage = 1;
    listState.failureError = null;
    listState.failureCount = 0;
    listState.loadPending = false;
  };

  const moveToNextMonth = () => {
    listState.currentToDate = subDays(listState.currentFromDate || new Date(), 1);
    listState.currentFromDate = getMaxDate(subMonths(listState.currentFromDate || new Date(), 1), getFromDate());
    listState.currentPage = 1;
  };

  const resetFilters = () => {
    typeFilter = ETypesFilter.NONE;
    dateFilter.DATE_PRESET = EDatePreset.ANY;
    dateFilter.TO = null;
    dateFilter.FROM = null;
    amountFilter.TO = null;
    amountFilter.FROM = null;
  };
  const reset = (shouldResetFilters?: boolean) => {
    localTransactionsRef.current = [];
    setTransactionsList([]);
    currFetchId.current = generateRandomId();
    const toDate = getToDate();
    listState.currentToDate = toDate;
    listState.currentFromDate = getMaxDate(getStartOfDay(getSubDaysFromBaseDate(toDate, 30)), getFromDate());
    listState.currentPage = 1;
    listState.sections = { pending: [], completed: [] };
    listState.failureError = null;
    listState.failureCount = 0;
    listState.loadPending = true;
    listState.state = ETransactionsState.None;

    if (shouldResetFilters) {
      resetFilters();
    }
  };

  function objectToURLSearchParams(obj: object): string {
    if ('startDate' in obj && 'endDate' in obj) {
      // @ts-ignore
      obj.startDate = formatToIsoString(obj.startDate);
      // @ts-ignore
      obj.endDate = formatToIsoString(obj.endDate);
    }
    const params = new URLSearchParams();
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
      // @ts-ignore
      if (obj[key] === null || obj[key] === undefined) {
        // eslint-disable-next-line
        continue;
      } else {
        // @ts-ignore
        params.append(key, obj[key]);
      }
    }
    return params.toString();
  }

  const generateQueryString = () => {
    const paramsObject: any = {};

    paramsObject.startDate = getFromDate();
    paramsObject.endDate = getToDate();

    if (typeFilter !== ETypesFilter.NONE) {
      // @ts-ignore
      paramsObject.transactionFilterType = typeFilter;
    }
    if (amountFilter.FROM && amountFilter.FROM >= 0) {
      // @ts-ignore
      paramsObject.amountFrom = amountFilter.FROM;
    }
    if (amountFilter.TO && amountFilter.TO > 0) {
      // @ts-ignore
      paramsObject.amountTo = amountFilter.TO;
    }
    return objectToURLSearchParams(paramsObject);
  };
  const loadTransactions = async (): Promise<IFetchTransactions> => {
    const paramsObject = {
      startDate: listState.currentFromDate,
      endDate: listState.currentToDate,
      page: listState.currentPage,
      recordCnt: pageMaxSize,
      pending: listState.loadPending,
    };
    if (typeFilter !== ETypesFilter.NONE) {
      // @ts-ignore
      paramsObject.transactionFilterType = typeFilter;
    }
    if (amountFilter.FROM && amountFilter.FROM >= 0) {
      // @ts-ignore
      paramsObject.amountFrom = amountFilter.FROM;
    }
    if (amountFilter.TO && amountFilter.TO > 0) {
      // @ts-ignore
      paramsObject.amountTo = amountFilter.TO;
    }
    const qString: any = objectToURLSearchParams(paramsObject);

    /** Store the current fetchId before Async function */
    const preFetchId = currFetchId.current;

    const res = await fetchTransactions({ queryParamsString: qString });

    /** check if fetchId changed during Async function */
    if (preFetchId !== currFetchId.current) {
      res.shouldAvoidResponse = true;
    }
    return res;
  };

  const didFinishLoadingTransactions = (fetchTransactionsResponse: IFetchTransactions) => {
    if (fetchTransactionsResponse.shouldAvoidResponse) return;
    if (!fetchTransactionsResponse.transactionList.length) {
      if (listState.loadPending) {
        changeStateToLoadSettledTransactions();
        loadTransactions().then(didFinishLoadingTransactions);
      } else if (isSameDate(new Date(listState.currentFromDate || ''), new Date(getFromDate()))) {
        listState.state = ETransactionsState.FullyLoaded;
      } else {
        moveToNextMonth();
        loadTransactions().then(didFinishLoadingTransactions);
      }
    } else {
      const mappedTransactions = fetchTransactionsResponse.transactionList.map((transaction) => ({
        ...transaction,
        id: transaction.transactionId,
        date: new Date(transaction.timestamp).toDateString(),
        name: transaction.merchantName,
        category: transaction.externalTransactionType || '',
        amount: transaction.amount,
        status: transaction.transactionStatus,
        externalTransactionType: transaction.externalTransactionType,
      }));

      const listToUpdate = [...localTransactionsRef.current, ...mappedTransactions];
      setTransactionsList(listToUpdate);
      localTransactionsRef.current = [...localTransactionsRef.current, ...mappedTransactions];

      if (listState.loadPending) {
        if (fetchTransactionsResponse.transactionList.length < pageMaxSize) {
          changeStateToLoadSettledTransactions();
          loadTransactions().then(didFinishLoadingTransactions);
        } else {
          listState.currentPage += 1;

          listState.state = ETransactionsState.Loaded;
        }
      } else if (fetchTransactionsResponse.transactionList.length < pageMaxSize) {
        if (isSameDate(new Date(listState.currentFromDate || ''), new Date(getFromDate()))) {
          listState.state = ETransactionsState.FullyLoaded;
        } else {
          moveToNextMonth();
          if (mappedTransactions.length < pageMinSize && localTransactionsRef.current.length < pageMinSize) {
            loadTransactions().then(didFinishLoadingTransactions);
          } else {
            listState.state = ETransactionsState.Loaded;
          }
        }
      } else {
        listState.currentPage += 1;
        if (mappedTransactions.length < pageMinSize && localTransactionsRef.current.length < pageMinSize) {
          loadTransactions().then(didFinishLoadingTransactions);
        } else {
          listState.state = ETransactionsState.Loaded;
        }
      }
    }
  };

  const start = (): void => {
    if (!(listState.state === ETransactionsState.None || listState.state === ETransactionsState.Failed)) {
      return;
    }
    reset();
    listState.state = ETransactionsState.Loading;
    loadTransactions()
      .then(didFinishLoadingTransactions)
      .catch((e: IServerErrorResponse) => {
        const error = e?.response?.data?.errors[0];
        if (error?.code === 4061) {
          if (isSameDate(new Date(listState.currentFromDate || ''), new Date(getFromDate()))) {
            listState.state = ETransactionsState.FullyLoaded;
          } else {
            moveToNextMonth();
            loadTransactions().then(didFinishLoadingTransactions);
          }
        } else {
          listState.failureError = e?.response?.data?.errors[0]?.message;
          listState.failureCount = 0;
          listState.state = ETransactionsState.Failed;
        }
      });
  };

  const loadMore = (): void => {
    if (listState.state === ETransactionsState.Loading || listState.state === ETransactionsState.FullyLoaded) {
      return;
    }
    listState.state = ETransactionsState.LoadingMore;
    loadTransactions()
      .then(didFinishLoadingTransactions)
      .catch((e) => {
        listState.failureError = listState.failureCount > 3 ? e.message : null;
        listState.failureCount += 1;
        listState.state = listState.failureCount > 3 ? ETransactionsState.Failed : ETransactionsState.Loaded;
      });
  };

  /** Handle Filter Type */
  const handleFilterTypeAndAmount = (type: ETypesFilter, amountData: { from: number; to: number }) => {
    /** Reset state */
    reset();
    /** Update typeFilter */
    typeFilter = type;
    /** Update Amount Filter */
    amountFilter = { ...amountFilter, FROM: amountData.from, TO: amountData.to };
    /** Call Start */
    start();
    setSelectedTransaction(null);
  };

  const handleFilterPeriod = ({
    startDate,
    endDate,
    presetType,
  }: {
    startDate: Date;
    endDate: Date;
    presetType: EDatePreset;
  }) => {
    /** Update period */
    dateFilter.DATE_PRESET = presetType;
    if (presetType === EDatePreset.CUSTOM) {
      dateFilter.FROM = getStartOfDay(startDate);
      dateFilter.TO = endDate;
    } else {
      dateFilter.FROM = null;
      dateFilter.TO = null;
    }
    reset();
    start();
    setSelectedTransaction(null);
  };

  return {
    start,
    reset,
    handleFilterTypeAndAmount,
    handleFilterPeriod,
    loadMore,
    localizedString,
    state: listState.state,
    currentUserHasLastYear,
    generateQueryString,
  };
};
